import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import OverlayPortal from "./OverlayPortal";

type SidekickState = {
  title: string;
  message?: string;
  autoDismissMs?: number;
};

type SidekickContextValue = {
  isSidekickActive: boolean;
  showSidekick: (payload?: { title?: string; message?: string; level?: number; autoDismissMs?: number }) => void;
  hideSidekick: () => void;
  state: SidekickState | null;
};

const SidekickContext = createContext<SidekickContextValue | null>(null);

export function SidekickProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SidekickState | null>(null);
  const dismissTimerRef = useRef<number | null>(null);

  const hideSidekick = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setState(null);
  }, []);

  const showSidekick = useCallback(
    (payload?: { title?: string; message?: string; level?: number; autoDismissMs?: number }) => {
      const title = payload?.title || "Level Up!";
      const message = payload?.message || (payload?.level ? `You reached Level ${payload.level}` : "Milestone reached");
      const autoDismissMs = payload?.autoDismissMs ?? 4000;

      setState({ title, message, autoDismissMs });
      if (dismissTimerRef.current) {
        window.clearTimeout(dismissTimerRef.current);
      }
      dismissTimerRef.current = window.setTimeout(() => {
        setState(null);
        dismissTimerRef.current = null;
      }, autoDismissMs);
    },
    []
  );

  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;

    (window as any).__debugShowSidekick = (payload?: { title?: string; message?: string; level?: number }) => {
      showSidekick(payload);
    };

    return () => {
      delete (window as any).__debugShowSidekick;
    };
  }, [showSidekick]);

  const value = useMemo(
    () => ({
      isSidekickActive: Boolean(state),
      showSidekick,
      hideSidekick,
      state,
    }),
    [hideSidekick, showSidekick, state]
  );

  return <SidekickContext.Provider value={value}>{children}</SidekickContext.Provider>;
}

export function useSidekick() {
  const context = useContext(SidekickContext);
  if (!context) {
    throw new Error("useSidekick must be used within SidekickProvider");
  }
  return context;
}

export function SidekickOverlay() {
  const { state, hideSidekick } = useSidekick();

  if (!state) {
    return null;
  }

  return (
    <OverlayPortal>
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{
          zIndex: 140,
          pointerEvents: "auto",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(0, 0, 0, 0.35)",
          }}
          onClick={hideSidekick}
        />
        <div
          className="relative rounded-2xl border-3 shadow-xl px-6 py-5 text-center max-w-[320px] w-[85vw]"
          style={{
            background: "linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)",
            borderColor: "#8b6542",
            color: "#3e2615",
            transform: "translateY(0)",
            transition: "transform 0.25s ease, opacity 0.25s ease",
          }}
        >
          <div className="text-lg font-bold mb-1">{state.title}</div>
          {state.message && <div className="text-sm font-semibold mb-4">{state.message}</div>}
          <button
            onClick={hideSidekick}
            className="w-full rounded-lg border-2 py-2 text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: "#fffaeb",
              borderColor: "#8b6542",
              color: "#3e2615",
              boxShadow: "0 2px 0 #8b6542",
            }}
          >
            Nice
          </button>
        </div>
      </div>
    </OverlayPortal>
  );
}
